import { createContext, ReactNode, useContext, useMemo, useRef } from 'react';
import { useFileCache } from '../../FileSystem';
import { PreWarmCacheManager } from './PreWarmCacheManager';

const PreWarmCacheContext = createContext<PreWarmCacheManager | undefined>(undefined);

/** Default when no offline source is wired in: assume online (attempt downloads). */
const useAlwaysOnline = () => false;

/**
 * Provides a single {@link PreWarmCacheManager} bound to the ambient FileCache.
 * Must be mounted inside a `FileCacheContextProvider`.
 *
 * Pass `useIsOffline` (mounted within a `KaiProApiStateProvider`) so the manager
 * skips downloads while offline and reports uncached files as `offlineIncomplete`
 * instead of attempting fetches that would fail.
 */
export const PreWarmCacheProvider = ({
  concurrency,
  verbose,
  useIsOffline = useAlwaysOnline,
  children,
}: {
  /** Max concurrent downloads within a single key's work (default 5). */
  concurrency?: number;
  /** Emit `[PreWarmCache:<key>]` trace logging (lifecycle + per-key summary). */
  verbose?: boolean;
  /** Hook returning the device's current offline status. Defaults to always-online. */
  useIsOffline?: () => boolean;
  children: ReactNode;
}) => {
  const fileCache = useFileCache();

  // Track offline status in a ref so the manager reads it fresh without being
  // re-created (and losing in-flight work) every time connectivity flips.
  const isOffline = useIsOffline();
  const isOfflineRef = useRef(isOffline);
  isOfflineRef.current = isOffline;

  const manager = useMemo(
    () => new PreWarmCacheManager(fileCache, { concurrency, verbose, isOffline: () => isOfflineRef.current }),
    [fileCache, concurrency, verbose],
  );
  return <PreWarmCacheContext.Provider value={manager}>{children}</PreWarmCacheContext.Provider>;
};

export const usePreWarmCacheManager = (): PreWarmCacheManager => {
  const manager = useContext(PreWarmCacheContext);
  if (!manager) {
    throw new Error('usePreWarmCacheManager must be used within a PreWarmCacheProvider');
  }
  return manager;
};
